import { Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { HttpEventType } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, finalize, map, of, switchMap } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { RadioButtonModule } from '@openng/optimus-ui/radiobutton';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { CurrencyFormatPipe, FileUploadServiceV2, rxResourceFixed } from '@zayan-erp/shared';
import { DsmDealOption } from '../../../models/dsm-deal-options-view.models';
import {
  DSM_BLOCKED_REASON_LABELS,
  DsmAddendumSelectionValidation,
  DsmBlockedAddendum,
  DsmSendAddendumSigningRequest,
  DsmSigningType,
  DsmUploadedDocumentVerification
} from '../../../models/dsm-addendum-sign-off.models';
import { DsmAddendumSignOffService } from '../../../services/dsm-addendum-sign-off.service';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';
import { DsmSignOffAddendumListComponent } from '../dsm-sign-off-addendum-list/dsm-sign-off-addendum-list.component';

type DocumentSource = 'Default' | 'Custom';

@Component({
  selector: 'dsm-send-for-sign-off-modal',
  standalone: true,
  imports: [
    ButtonModule,
    RadioButtonModule,
    ToggleSwitchModule,
    TooltipModule,
    FormsModule,
    CurrencyFormatPipe,
    DsmSignOffAddendumListComponent
  ],
  templateUrl: './dsm-send-for-sign-off-modal.component.html'
})
export class DsmSendForSignOffModalComponent {
  readonly #signOffService = inject(DsmAddendumSignOffService);
  readonly #dealOptionsService = inject(DsmDealOptionsService);
  readonly #fileUploadService = inject(FileUploadServiceV2);
  readonly #toastr = inject(ToastrService);
  readonly #destroyRef = inject(DestroyRef);

  readonly dealId = input.required<string>();
  readonly addendums = input.required<DsmDealOption[]>();
  readonly lotNo = input<string | null>(null);

  readonly sent = output<void>();
  readonly viewEnvelope = output<string>();
  readonly dialogClose = output<void>();

  readonly includeBuilder = signal<boolean>(false);
  readonly documentSource = signal<DocumentSource>('Default');
  readonly customDocument = signal<{ name: string; s3DocumentId: string } | null>(null);
  readonly verification = signal<DsmUploadedDocumentVerification | null>(null);
  readonly customDocumentError = signal<string | null>(null);
  readonly acknowledgeMissingSigners = signal<boolean>(false);
  readonly isUploading = signal<boolean>(false);
  readonly isPreviewingDefaultDocument = signal<boolean>(false);

  /** Which signing action is in flight — lets only the clicked button show its own spinner. */
  readonly #pendingSigningType = signal<DsmSigningType | null>(null);
  readonly isSubmitting = computed(() => this.#pendingSigningType() !== null);
  readonly isSigningNow = computed(() => this.#pendingSigningType() === DsmSigningType.DocuSignInPerson);
  readonly isSendingViaEmail = computed(() => this.#pendingSigningType() === DsmSigningType.DocuSign);

  readonly selectedIds = computed(() => this.addendums().map(addendum => addendum.id));

  readonly validationResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId(), addendumIds: this.selectedIds() }),
    defaultValue: null as DsmAddendumSelectionValidation | null,
    stream: ({ params }) => {
      if (!params.dealId || !params.addendumIds.length) return of(null);
      return this.#signOffService.validateSelection(params.dealId, params.addendumIds);
    }
  });

  readonly isValidating = computed(() => this.validationResource.isLoading());

  readonly blockedById = computed(() => {
    const blocked = this.validationResource.value()?.blockedAddendums ?? [];
    return new Map<string, DsmBlockedAddendum>(blocked.map(item => [item.addendumId, item]));
  });

  readonly blockedAddendums = computed(() => this.addendums().filter(addendum => this.blockedById().has(addendum.id)));
  readonly sendableAddendums = computed(() =>
    this.addendums().filter(addendum => !this.blockedById().has(addendum.id))
  );
  readonly hasBlockedAddendums = computed(() => this.blockedAddendums().length > 0);
  readonly blockedIds = computed(() => this.blockedAddendums().map(addendum => addendum.id));

  readonly total = computed(() => this.sendableAddendums().reduce((sum, addendum) => sum + (addendum.total ?? 0), 0));
  readonly sendableIds = computed(() => this.sendableAddendums().map(addendum => addendum.id));

  readonly isCustomSource = computed(() => this.documentSource() === 'Custom');

  readonly blockingErrors = computed(() => this.verification()?.blockingErrors ?? []);
  readonly warnings = computed(() => this.verification()?.warnings ?? []);
  readonly missingSigners = computed(() => this.verification()?.missingRequiredSigners ?? []);
  readonly needsAcknowledgement = computed(
    () => this.missingSigners().length > 0 && this.blockingErrors().length === 0
  );

  readonly isSubmitDisabled = computed(
    () =>
      this.isSubmitting() ||
      this.isUploading() ||
      this.isValidating() ||
      this.hasBlockedAddendums() ||
      this.sendableAddendums().length === 0 ||
      (this.isCustomSource() &&
        (!this.customDocument() ||
          this.blockingErrors().length > 0 ||
          (this.needsAcknowledgement() && !this.acknowledgeMissingSigners())))
  );

  isBlocked(addendum: DsmDealOption): boolean {
    return this.blockedById().has(addendum.id);
  }

  blockedReason(addendum: DsmDealOption): string {
    const blocked = this.blockedById().get(addendum.id);
    if (!blocked) return '';
    return DSM_BLOCKED_REASON_LABELS[blocked.reason] ?? 'Cannot be sent';
  }

  selectDocumentSource(source: DocumentSource): void {
    this.documentSource.set(source);
    if (source === 'Default') this.customDocumentError.set(null);
  }

  onViewEnvelope(addendum: DsmDealOption): void {
    const dealDocumentId =
      this.blockedById().get(addendum.id)?.existingDealDocumentId ?? addendum.signOff?.dealDocumentId ?? null;
    if (dealDocumentId) this.viewEnvelope.emit(dealDocumentId);
  }

  canViewEnvelope(addendum: DsmDealOption): boolean {
    return !!(this.blockedById().get(addendum.id)?.existingDealDocumentId ?? addendum.signOff?.dealDocumentId);
  }

  viewDefaultDocument(): void {
    const dealId = this.dealId();
    const addendumIds = this.sendableIds();
    if (!dealId || !addendumIds.length || this.isPreviewingDefaultDocument()) return;

    this.isPreviewingDefaultDocument.set(true);
    this.#dealOptionsService
      .getPrintAddendumOptions()
      .pipe(
        switchMap(options => {
          const defaultOptionIds = options.filter(option => option.isDefault).map(option => option.id);
          const optionIds = defaultOptionIds.length ? defaultOptionIds : options.map(option => option.id);
          return this.#dealOptionsService.downloadAddendumsPdf(
            dealId,
            addendumIds,
            optionIds,
            `${this.lotNo() ?? 'Addendums'}-Sign-off.pdf`,
            true
          );
        }),
        finalize(() => this.isPreviewingDefaultDocument.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  onCustomDocumentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.documentSource.set('Custom');
    this.isUploading.set(true);
    this.customDocument.set(null);
    this.verification.set(null);
    this.customDocumentError.set(null);
    this.acknowledgeMissingSigners.set(false);

    this.#fileUploadService
      .getPrivateAWSSignedUrl(file)
      .pipe(
        switchMap(awsData =>
          this.#fileUploadService.uploadFileToS3WithProgress(awsData).pipe(
            filter(event => event.type === HttpEventType.Response),
            map(() => awsData.documentId)
          )
        ),
        switchMap(s3DocumentId =>
          this.#signOffService
            .verifyUploadedDocument(this.dealId(), s3DocumentId, this.sendableIds())
            .pipe(map(result => ({ s3DocumentId, result })))
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: ({ s3DocumentId, result }) => {
          this.isUploading.set(false);
          this.customDocument.set({ name: file.name, s3DocumentId });
          this.verification.set(result);
          if (result.blockingErrors.length) {
            this.#raiseError(result.blockingErrors[0]);
          } else if (result.missingRequiredSigners.length) {
            this.#raiseError(`Upload is missing a signer: ${result.missingRequiredSigners.join(', ')}`);
          }
        },
        error: (error: unknown) => {
          this.isUploading.set(false);
          this.#raiseError(this.#extractErrorMessage(error));
        }
      });
  }

  signNow(): void {
    this.#send(DsmSigningType.DocuSignInPerson);
  }

  sendViaEmail(): void {
    this.#send(DsmSigningType.DocuSign);
  }

  close(): void {
    this.dialogClose.emit();
  }

  #send(signingType: DsmSigningType): void {
    if (this.isSubmitDisabled()) return;

    const request: DsmSendAddendumSigningRequest = {
      addendumIds: this.sendableIds(),
      includeBuilder: this.includeBuilder(),
      signingType,
      s3DocumentId: this.isCustomSource() ? (this.customDocument()?.s3DocumentId ?? null) : null,
      acknowledgeMissingSigners: this.isCustomSource() && this.acknowledgeMissingSigners()
    };

    this.#pendingSigningType.set(signingType);
    this.customDocumentError.set(null);
    this.#signOffService
      .send(this.dealId(), request)
      .pipe(
        finalize(() => this.#pendingSigningType.set(null)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: () => this.sent.emit(),
        error: (error: unknown) => this.#raiseError(this.#extractErrorMessage(error))
      });
  }

  #raiseError(message: string): void {
    this.customDocumentError.set(message);
    this.#toastr.error(message, 'Error');
  }

  #extractErrorMessage(error: unknown): string {
    const fallback = 'Upload is missing a scan or missing a Signer';
    const body = (error as { error?: { message?: string } } | null)?.error;
    return body?.message?.trim() || fallback;
  }
}
